<script lang="ts" setup>
import { computed } from "vue";
import { Trash2 } from "@lucide/vue";
import Table from "@/components/Reusables/Table/Table.vue";
import type { Column } from "@/components/Reusables/Table/types";
import { MachineryFuelEmissionFactorItem } from "@/features/BuildingProject/providers/InstallationEmissionFactorsProvider";
import format from "@/services/Utils/format";

const props = defineProps<{
  considerationPeriod: number;
  denominatorUnit?: string;
  items: MachineryFuelEmissionFactorItem[];
  scaledEmissionUnit?: string;
  totalArea: number;
}>();

const emit = defineEmits<{
  (e: "remove", id: string): void;
}>();

const handleRemove = (id: string) => {
  emit("remove", id);
};

const formatValue = ({ value, unit }: { value: number; unit: string }) => {
  const formatted = format.decimalNumber(value);
  const cleaned = formatted.match(/^[\d.,]+/)?.[0];

  // Check if it's a whole number after formatting (e.g., "42.00" or "42,00")
  const isWhole = cleaned?.endsWith(".00") || cleaned?.endsWith(",00");

  const finalValue = isWhole ? cleaned?.slice(0, -3) : formatted;

  return `${finalValue} ${unit || ""}`;
};

const calculateScaledEmission = (totalEmission: number) => {
  const value = totalEmission / props.considerationPeriod / props.totalArea;
  return {
    unit: props.scaledEmissionUnit || "",
    value: value === Infinity ? 0 : value,
  };
};

type ItemRow = MachineryFuelEmissionFactorItem & Record<string, unknown>;

const columns = computed<Column[]>(() => {
  const cols: Column[] = [
    { key: "dataSourceName", label: "Data source" },
    { key: "emissionFactorLabel", label: "Emission factor" },
    { key: "inputs", label: "Inputs" },
    { key: "totalEmission", label: "Total emission", align: "right" },
  ];
  if (props.scaledEmissionUnit) {
    cols.push({ key: "scaledEmission", label: "Total scaled emission", align: "right" });
  }
  cols.push({ key: "remove", label: "" });
  return cols;
});
</script>

<template>
  <div class="col-span-12">
    <Table
      caption="Machinery fuel emission factors"
      :columns="columns"
      :rows="items as ItemRow[]"
      :row-key="(item) => item.id">
      <template #cell-inputs="{ row }">
        {{ (row as MachineryFuelEmissionFactorItem).inputs.map(formatValue).join(", ") }}
      </template>
      <template #cell-totalEmission="{ row }">
        {{ formatValue((row as MachineryFuelEmissionFactorItem).totalEmission) }}
      </template>
      <template
        v-if="scaledEmissionUnit"
        #cell-scaledEmission="{ row }">
        {{ formatValue(calculateScaledEmission((row as MachineryFuelEmissionFactorItem).totalEmission.value)) }}
      </template>
      <!-- The remove column has no visible title; a sr-only one keeps the header cell non-empty for AT. -->
      <template #header-remove>
        <span class="sr-only">Actions</span>
      </template>
      <template #cell-remove="{ row }">
        <button
          type="button"
          aria-label="Remove emission factor"
          class="focus-ring interactive-active duration-fast ease-standard rounded-4 ml-auto flex p-1 transition-colors hover:bg-slate-100"
          @click="handleRemove((row as MachineryFuelEmissionFactorItem).id)">
          <Trash2
            class="icon-sm text-app-red-600"
            :stroke-width="1.5" />
        </button>
      </template>
    </Table>
  </div>
</template>
